/**
 * Утилита для извлечения URL из текста и загрузки meta-тегов (Open Graph,
 * Twitter Cards, стандартные meta) через CORS-прокси для превью ссылок.
 *
 * Кэш in-memory на 10 минут, чтобы избежать повторных запросов при ререндере.
 */

// CORS-прокси. allorigins возвращает JSON { contents, status }.
const CORS_PROXIES = [
    'https://api.allorigins.win/get?url=',
    'https://corsproxy.io/?url=',
];

const CACHE_TTL = 10 * 60 * 1000; // 10 минут
const cache = new Map(); // url → { data, fetchedAt }

/**
 * Регулярное выражение для поиска URL в тексте.
 * Поддерживает http(s):// и www. (без протокола).
 */
const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi;

/**
 * Извлекает все URL из текста сообщения.
 * @param {string} text
 * @returns {string[]} — массив URL
 */
export function extractUrls(text) {
    if (!text) return [];
    const matches = text.match(URL_REGEX);
    if (!matches) return [];
    // Убирааем trailing punctuation, которую regex мог захватить
    return matches.map(url => url.replace(/[.,;:!?)\]}>]+$/, ''));
}

/**
 * Парсит HTML-строку и извлекает meta-теги (OG, Twitter, стандартные).
 * Использует DOMParser (доступен в браузере).
 *
 * @param {string} html
 * @returns {{ title: string, description: string, image: string, siteName: string, url: string }|null}
 */
function parseMetaTags(html) {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const getMeta = (selector, attr = 'content') => {
            const el = doc.querySelector(selector);
            return el?.getAttribute(attr)?.trim() || null;
        };

        // Приоритет: Open Graph → Twitter Card → стандартные meta → <title>
        const title =
            getMeta('meta[property="og:title"]') ||
            getMeta('meta[name="twitter:title"]') ||
            getMeta('meta[name="title"]') ||
            doc.querySelector('title')?.textContent?.trim() ||
            null;

        const description =
            getMeta('meta[property="og:description"]') ||
            getMeta('meta[name="twitter:description"]') ||
            getMeta('meta[name="description"]') ||
            null;

        let image =
            getMeta('meta[property="og:image"]') ||
            getMeta('meta[name="twitter:image"]') ||
            getMeta('meta[name="twitter:image:src"]') ||
            getMeta('link[rel="image_src"]', 'href') ||
            null;

        const siteName =
            getMeta('meta[property="og:site_name"]') ||
            null;

        const url =
            getMeta('meta[property="og:url"]') ||
            getMeta('meta[name="twitter:url"]') ||
            null;

        // Делаем image абсолютным URL, если он относительный
        if (image && !image.startsWith('http')) {
            try {
                const baseUrl = url || doc.querySelector('base')?.href;
                if (baseUrl) {
                    image = new URL(image, baseUrl).href;
                }
            } catch {
                // оставляем как есть
            }
        }

        if (!title && !description && !image) return null;

        return { title, description, image, siteName, url };
    } catch {
        return null;
    }
}

/**
 * Загружает meta-теги для URL через CORS-прокси.
 * Пробует несколько прокси по очереди.
 *
 * @param {string} url
 * @returns {Promise<{ title: string, description: string, image: string, siteName: string, url: string }|null>}
 */
export async function fetchLinkPreview(url) {
    // Проверяем кэш
    const cached = cache.get(url);
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
        return cached.data;
    }

    for (const proxy of CORS_PROXIES) {
        try {
            const proxyUrl = proxy + encodeURIComponent(url);

            const response = await fetch(proxyUrl, {
                signal: AbortSignal.timeout?.(8000),
            });

            if (!response.ok) continue;

            let html;
            if (proxy.startsWith('https://api.allorigins.win')) {
                // allorigins возвращает JSON { contents, status }
                const json = await response.json();
                html = json.contents;
            } else {
                // corsproxy.io возвращает raw HTML
                html = await response.text();
            }

            if (!html || html.length < 50) continue;

            const meta = parseMetaTags(html);
            if (meta) {
                cache.set(url, { data: meta, fetchedAt: Date.now() });
                return meta;
            }
        } catch {
            // пробуем следующий прокси
            continue;
        }
    }

    // Все прокси не сработали — кэшируем null чтобы не повторять
    cache.set(url, { data: null, fetchedAt: Date.now() });
    return null;
}

/**
 * Очистка кэша (например, при logout).
 */
export function clearLinkPreviewCache() {
    cache.clear();
}

/**
 * Извлекает домен из URL для отображения в карточке.
 * @param {string} url
 * @returns {string}
 */
export function getDomainFromUrl(url) {
    try {
        const u = new URL(url);
        return u.hostname.replace(/^www\./, '');
    } catch {
        return url;
    }
}
